import usePagination from "@/hooks/usePagination";
import Pager from "@/components/Pager";

/*
  Pagination for the lists that aren't tables — a member's leave history, a
  conversation list, a comment timeline. DataTable already gives every grid a
  pager and a "Showing x-y of z" footer; this gives the same two things to a
  list that renders its own rows, so the app has one pagination behaviour rather
  than one per component.

  Render-prop rather than `children` directly, because the caller owns the
  markup between the wrapper and the rows: some of these lists are a
  `<div className="space-y-3">`, some are a bordered card, one is a table body.
  Passing the current page's slice back lets each keep its own shape.

      <PagedList items={leaves} pageSize={10} testIdPrefix="my-leaves">
        {(page) => page.map((l) => <LeaveRow key={l._id} leave={l} />)}
      </PagedList>

  usePagination already resets to page 1 whenever the item count changes, so a
  filter, a search or a deletion can't strand the reader on a page that no
  longer exists.
*/
export default function PagedList({
  items = [],
  pageSize = 10,
  children,
  testIdPrefix = "list",
  className = "",
  // Slotted into the footer beside the count — a legend, a bulk action.
  footerExtra = null,
}) {
  const { page, setPage, pageCount, pageItems, totalItems, pageSize: size } = usePagination(items, pageSize);

  return (
    <div className={className}>
      {children(pageItems)}

      {totalItems > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 pt-3 mt-3 border-t border-border text-xs text-zinc-500">
          <span>
            Showing{" "}
            <span className="font-mono text-zinc-700">
              {(page - 1) * size + 1}{"–"}{Math.min(page * size, totalItems)}
            </span>{" "}
            of <span className="font-mono text-zinc-700">{totalItems}</span>
          </span>
          <div className="flex items-center gap-3">
            {footerExtra}
            <Pager page={page} pageCount={pageCount} onPage={setPage} testIdPrefix={testIdPrefix} />
          </div>
        </div>
      )}
    </div>
  );
}
